// src/Services/Permissions.ts

export interface UserDetails {
  id?: string;
  is_ceo?: boolean;
  is_shareholder?: boolean;
  is_success_manager?: boolean;
  [key: string]: any;
}

export interface PagePermissions {
  my_reports?: boolean;
  meeting_schedule?: boolean;
  strategy?: boolean;
  feedback?: boolean;
  review?: boolean;
  recurring_activities?: boolean;
  kpis?: boolean;
  forecast?: boolean;
  processes?: boolean;
  initiatives?: boolean;
  planning?: boolean;
  objectives?: boolean;
  values_and_principles?: boolean;
  [key: string]: any;
}

/**
 * Checks if a user can write/mutate data (general check).
 * Shareholders cannot mutate unless specifically granted.
 */
export const canWrite = (user: UserDetails | null | undefined): boolean => {
  if (!user) return false;
  // Shareholders have read-only access globally, except for few whitelisted endpoints.
  // For UI rendering (hiding/showing buttons), we assume they cannot write.
  if (user.is_shareholder) return false;
  return true;
};

/**
 * Checks if a user has CEO-level capabilities.
 */
export const isUserCeo = (user: UserDetails | null | undefined): boolean => {
  return !!user?.is_ceo;
};

/**
 * Checks if a user is a shareholder.
 */
export const isUserShareholder = (user: UserDetails | null | undefined): boolean => {
  return !!user?.is_shareholder;
};

/**
 * Checks if a user is a success manager.
 */
export const isUserSuccessManager = (user: UserDetails | null | undefined): boolean => {
  return !!user?.is_success_manager;
};

/**
 * Checks if a user can access a specific module based on page permissions or CEO status.
 */
export const canAccessPage = (
  user: UserDetails | null | undefined,
  pagePermissions: PagePermissions | null | undefined,
  pageKey: keyof PagePermissions
): boolean => {
  if (!user) return false;
  if (user.is_ceo) return true;
  return !!Object.entries(pagePermissions || {}).find(([k]) => k === pageKey)?.[1];
};

/**
 * Checks if a user can manage settings for a specific report/resource.
 */
export const canManageReportSettings = (
  user: UserDetails | null | undefined,
  resourceOwnerId: string | null | undefined
): boolean => {
  if (!user) return false;
  if (user.is_shareholder) return false;
  if (user.is_success_manager) return true;
  return user.id === resourceOwnerId;
};

/**
 * Checks if a user can manage (add/edit/delete) users/shareholders.
 */
export const canManageUsers = (user: UserDetails | null | undefined): boolean => {
  if (!user) return false;
  return !!user.is_ceo;
};

/**
 * Checks if a user can interact with meeting records (like deleting comments or managing agendas).
 */
export const canManageMeetingRecord = (
  user: UserDetails | null | undefined,
  resourceOwnerId: string | null | undefined
): boolean => {
  if (!user) return false;
  if (user.is_shareholder) return false;
  if (user.is_success_manager) return true;
  return user.id === resourceOwnerId;
};

/**
 * Generalized check if the user is the owner or a success manager.
 */
export const isOwnerOrSuccessManager = (
  user: UserDetails | null | undefined,
  resourceOwnerId: string | null | undefined
): boolean => {
  if (!user) return false;
  if (user.is_success_manager) return true;
  return user.id === resourceOwnerId;
};
